import { useEffect } from "react";

const DEFAULTS = {
  siteName: "Westbridge Inn & Suites",
  baseUrl: "https://www.westbridgecarrollton.com",
  defaultImage:
    "https://www.westbridgecarrollton.com/images/property/exterior-main.jpg",
};

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * useSEO — sets title, meta description, OG tags, canonical, and optional JSON-LD.
 * Pass { title, description, path, image, jsonLd } — jsonLd can be one object or an array.
 */
export function useSEO({ title, description, path = "/", image, jsonLd } = {}) {
  useEffect(() => {
    const fullTitle = title
      ? title.includes("Westbridge")
        ? title
        : `${title} · ${DEFAULTS.siteName}`
      : `${DEFAULTS.siteName} · Carrollton, Missouri`;
    document.title = fullTitle;

    const desc =
      description ||
      "Westbridge Inn & Suites — 34 clean, comfortable rooms on US-65 in Carrollton, Missouri. Free hot breakfast, pet friendly, truck parking and a 24-hour front desk.";

    const url = `${DEFAULTS.baseUrl}${path}`;
    const img = image || DEFAULTS.defaultImage;

    setMeta("description", desc);
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", img, "property");
    setMeta("og:site_name", DEFAULTS.siteName, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", img);

    setLink("canonical", url);

    // JSON-LD
    const cleanIds = ["jsonld-page-1", "jsonld-page-2", "jsonld-page-3"];
    cleanIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.remove();
    });

    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((d, i) => setJsonLd(`jsonld-page-${i + 1}`, d));
    }
  }, [title, description, path, image, JSON.stringify(jsonLd)]); // eslint-disable-line
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${DEFAULTS.baseUrl}${it.path}`,
    })),
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

export function articleLd({ title, description, image, slug, datePublished }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image,
    author: { "@type": "Organization", name: DEFAULTS.siteName },
    publisher: {
      "@type": "Organization",
      name: DEFAULTS.siteName,
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_0eab4c7f-6e15-47fa-b0e0-619340f56efd/artifacts/sinu4mcv_Small%20logo.png",
      },
    },
    datePublished: datePublished || new Date().toISOString(),
    mainEntityOfPage: `${DEFAULTS.baseUrl}/blog/${slug}`,
  };
}
